<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="modelValue"
        class="base-modal-overlay"
        role="dialog"
        :aria-modal="true"
        :aria-labelledby="titleId"
        @mousedown.self="handleBackdropClick"
      >
        <div
          class="base-modal-container"
          :class="[sizeClass, customClass]"
          :style="{ maxWidth: maxWidth || undefined }"
        >
          <!-- Header -->
          <header v-if="$slots.header || title" class="base-modal-header">
            <slot name="header">
              <h3 :id="titleId" class="base-modal-title">{{ title }}</h3>
              <p v-if="subtitle" class="base-modal-subtitle">{{ subtitle }}</p>
            </slot>
            <button
              v-if="showClose"
              type="button"
              class="base-modal-close"
              aria-label="关闭"
              @click="close"
            >×</button>
          </header>

          <!-- Body -->
          <div class="base-modal-body" :class="{ 'no-padding': noBodyPadding }">
            <slot />
          </div>

          <!-- Footer -->
          <footer v-if="$slots.footer" class="base-modal-footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'md', // sm, md, lg, xl, full
    validator: val => ['sm', 'md', 'lg', 'xl', 'full'].includes(val),
  },
  maxWidth: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  closeOnEsc: {
    type: Boolean,
    default: true,
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  customClass: {
    type: String,
    default: '',
  },
  noBodyPadding: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'close', 'open']);

const titleId = computed(() => `modal-title-${Math.random().toString(36).slice(2, 9)}`);
const sizeClass = computed(() => `size-${props.size}`);

function close() {
  emit('update:modelValue', false);
  emit('close');
}

function handleBackdropClick() {
  if (props.closeOnBackdrop) {
    close();
  }
}

function handleKeydown(e) {
  if (e.key === 'Escape' && props.modelValue && props.closeOnEsc) {
    close();
  }
}

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    emit('open');
  }
});

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.base-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.base-modal-container {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  max-height: min(90vh, 800px);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.size-sm { max-width: 400px; }
.size-md { max-width: 520px; }
.size-lg { max-width: 680px; }
.size-xl { max-width: 860px; }
.size-full { max-width: min(1140px, 94vw); height: min(88vh, 860px); }

.base-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px 14px;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.base-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--text-dark, #0f172a);
  line-height: 1.35;
}

.base-modal-subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-muted, #64748b);
}

.base-modal-close {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
}

.base-modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.base-modal-body {
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1;
}

.base-modal-body.no-padding {
  padding: 0;
}

.base-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid var(--border-color, #e2e8f0);
  background: #f8fafc;
}

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .base-modal-container {
  animation: modalPopIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-fade-leave-active .base-modal-container {
  animation: modalPopOut 0.15s ease forwards;
}

@keyframes modalPopIn {
  0% { transform: scale(0.95) translateY(8px); }
  100% { transform: scale(1) translateY(0); }
}

@keyframes modalPopOut {
  0% { transform: scale(1) translateY(0); }
  100% { transform: scale(0.96) translateY(6px); }
}
</style>
